import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { removeDuplicates } from 'utils/happyFunctions';
import {
  QuestionBody,
  QuestionChoiceContainer,
  QuestionWrapper,
  RemoveSelectedIcon,
} from './styles';
import Select from 'components/Select';
import TitleInput from './TitleInput';
import { IOption } from 'components/Select/Select';
import DescriptionInput, {
  getDescriptionInputStatus,
} from './DescriptionInput';
import { ITags, ITagView } from 'store/providers/providerTypes';
import {
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  IAnswerView,
  IQuestionsView,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { ICompany } from 'store/companies/companiesTypes';
import Input from '../Input/Input';
import i18n from '../../i18n';
import { isANumber } from '../../utils/number';

interface IComponentProps {
  tags?: ITags['data'];
  answer?: IMultipleChoiceAnswerView;
  isUnique: boolean;
  isRequired: boolean;
  onChange: (newAnswer: IMultipleChoiceAnswerView, questionId: number) => void;
  question: IMultipleChoiceQuestionView;
  disabled?: boolean;
  distinctiveValues?: { [key: string]: string };
  isEditor: boolean;
  company?: ICompany;
  currentUserCompanyRoles?: number[];
  randomizeOrder?: { [key: number]: number };
  survey?: ISurveyView;
  draft?: boolean;
  answers?: { [key: number]: IAnswerView };
  questions?: { [questionId: number]: IQuestionsView };
}

interface IComponentStates {
  addDescription: boolean;
  windowWidth: number;
}

class MultiChoiceQuestion extends React.Component<
  IComponentProps,
  IComponentStates
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      addDescription:
        !props.answer || (props.answer && !props.answer.description),
      windowWidth: window.innerWidth,
    };
  }

  componentDidMount() {
    // add distinctiveValues to form answers
    if (this.props.distinctiveValues && this.props.question.distinctive) {
      this.updateDistinctiveAnswers();
    }
  }

  componentDidUpdate(prevProps: IComponentProps) {
    // update distinctiveValues in form answers onClick
    if (
      this.props.distinctiveValues &&
      prevProps.distinctiveValues !== this.props.distinctiveValues &&
      this.props.question.distinctive
    ) {
      this.updateDistinctiveAnswers();
    }
  }

  updateDistinctiveAnswers() {
    const { answer, question, onChange, distinctiveValues } = this.props;
    if (distinctiveValues) {
      if (distinctiveValues[question.id]) {
        let distinctiveAnswers: any;
        if (question.useTags) {
          const selected = [distinctiveValues[question.id]];
          distinctiveAnswers = {
            ...answer,
            selected,
            value: undefined,
          };
        } else {
          distinctiveAnswers = {
            ...answer,
            value: distinctiveValues[question.id],
            selected: [],
          };
        }
        onChange(distinctiveAnswers, question.id);
      } else {
        const newAnswer: any = {
          ...answer,
          selected: [],
          values: [],
          value: undefined,
        };
        onChange(newAnswer, question.id);
      }
    }
  }

  descriptionHandler = () => {
    this.setState({
      addDescription: !this.state.addDescription,
    });
  };

  handleChange = (value: string, name: React.ReactText) => {
    const { answer, question, onChange } = this.props;
    const newAnswer: any = answer ? cloneDeep(answer) : {};
    if (name === 'description') {
      newAnswer.description = value;
    } else {
      const isMultiSelect = question.multiSelect;
      if (question.useTags) {
        let selected;
        const val = Number(value);
        if (answer && answer.selected && isMultiSelect) {
          if (answer.selected.includes(val)) {
            selected = answer.selected.filter((item) => item !== val);
          } else {
            selected = removeDuplicates([...answer.selected, val]);
          }
        } else {
          selected = [val];
        }
        newAnswer.selected = selected;
      } else {
        let values;
        if (answer && isMultiSelect) {
          if (
            (answer.values && answer.values.includes(value)) ||
            answer.value === value
          ) {
            values = answer.values.filter((item) => item !== value);
            newAnswer.value = null;
          } else {
            values = removeDuplicates([...answer.values, value]);
          }
        } else {
          if (answer && answer.value) {
            newAnswer.value = null;
          }
          values = [value];
        }
        newAnswer.values = values;
      }
    }
    onChange(newAnswer, question.id);
  };

  handleRemoveSelectedChoice = (event: React.MouseEvent<HTMLElement>) => {
    const {
      currentTarget: {
        dataset: { choiceValue, useTags },
      },
    } = event;

    const { answer, question, onChange } = this.props;
    const newAnswer: any = cloneDeep(answer);

    if (useTags === 'true' && newAnswer.selected) {
      newAnswer.selected = newAnswer.selected.filter(
        (tagValue: number) => `${tagValue}` !== choiceValue
      );
    } else {
      if (newAnswer.value) {
        newAnswer.value = null;
      }
      newAnswer.values = newAnswer.values.filter(
        (value: React.ReactText) => value !== choiceValue
      );
    }
    onChange(newAnswer, question.id);
  };

  handleSelectChange = (chips: IOption[], selectIndex?: number) => {
    const { answer, question, onChange } = this.props;
    const goDeepInTags = question.goDeepInTags || 0;

    const newAnswer: any = answer ? cloneDeep(answer) : {};
    const choices = chips.map((chip) => chip.value);
    newAnswer.value = null;
    if (question.useTags) {
      if (goDeepInTags > 0 && isANumber(selectIndex) && selectIndex! >= 0) {
        newAnswer.selected = [
          ...(newAnswer?.selected?.slice(0, selectIndex!) || []),
          ...choices,
        ];
      } else {
        newAnswer.selected = choices;
        newAnswer.values = [];
      }
    } else {
      newAnswer.selected = [];
      newAnswer.values = choices;
    }
    // if answer is required add error message when choices are empty
    if (question.answerRequired) {
      if (chips.length === 0) {
        newAnswer.error = {
          emptyChoices: i18n.t('emptyChoicesError', { ns: 'survey' }),
        };
      } else if (newAnswer.error) {
        // to delete existing error
        delete newAnswer['error'];
      }
    }
    onChange(newAnswer, question.id);
  };

  shouldComponentUpdate(
    nextProps: Readonly<IComponentProps>,
    nextState: Readonly<IComponentStates>
  ): boolean {
    return (
      nextProps.answer !== this.props.answer ||
      nextProps.question !== this.props.question ||
      nextProps.isRequired !== this.props.isRequired ||
      nextProps.distinctiveValues !== this.props.distinctiveValues ||
      nextProps.tags !== this.props.tags ||
      nextState !== this.state
    );
  }

  render() {
    const {
      question,
      answer,
      tags,
      isRequired,
      disabled,
      isEditor,
      company,
      currentUserCompanyRoles,
      draft,
      survey,
    } = this.props;

    const descriptionStatus = survey
      ? getDescriptionInputStatus(survey, question)
      : true;

    const viewMode: IMultipleChoiceQuestionView['viewMode'] = question.viewMode;
    const permissionScheme = isEditor
      ? []
      : question.fillPermissionSchemeList || [];
    let validChoices: number[] | undefined;
    if (company && permissionScheme.length > 0) {
      validChoices = [];
      permissionScheme.forEach((ps) => {
        validChoices = [
          ...(validChoices || []),
          ...Object.entries(
            company?.permissionSchemes?.[ps]?.properties?.roleIdTagIdListMap ??
              {}
          ).reduce((current: number[], [key, value]) => {
            if (
              currentUserCompanyRoles &&
              currentUserCompanyRoles.includes(Number(key))
            ) {
              current = [...current, ...value];
            }
            return current;
          }, [] as number[]),
        ];
      });
    } else {
      validChoices = undefined;
    }
    let choices: IMultipleChoiceQuestionView['choiceList'] | Array<number> = [];
    if (question.useTags && tags) {
      // todo: parent tags could be null instead of array
      if (question.parentTagChoices) {
        question.parentTagChoices.forEach((parentId: number) => {
          //todo should check for null
          Object.values(tags)
            .filter(
              (tag) =>
                tag.parentId === parentId &&
                !tag.deleted &&
                !question.excludedTagChoices?.includes(Number(tag.id)) &&
                (validChoices?.includes(Number(tag.id)) ?? true)
            )
            .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
            .map((item) => {
              // @ts-expect-error type changing taking place
              choices.push(item.id);
            });
        });
      }
      if (question.tagChoices) {
        question.tagChoices.forEach((tagChoice: number) => {
          if (!validChoices || validChoices.includes(tagChoice))
            choices.push(tagChoice as any);
        });
      }
      choices = removeDuplicates(choices as Array<number>);
    } else {
      choices = question.choiceList.map((it, index) => ({
        ...it,
        index: index,
      }));
      if (this.props.randomizeOrder)
        choices = choices.sort(
          (a: any, b: any) =>
            this.props.randomizeOrder![(a.index * 31 + question.id) % 100] -
            this.props.randomizeOrder![(b.index * 31 + question.id) % 100]
        );
    }

    const isMultiSelect = question.multiSelect;
    const useTags = question.useTags;
    const hiddenForBeingDistinctive = question.distinctive;
    const finalRequired = hiddenForBeingDistinctive
      ? false
      : question.answerRequired;
    const hasDepth = (question.goDeepInTags || 0) > 0;

    const isMobileView = this.state.windowWidth < 600;
    const isTabletView = this.state.windowWidth < 800 && !isMobileView;

    let colNum: null | number = null;
    let isInlineDisplayMode: boolean = false;
    if (viewMode !== 'popup') {
      colNum = isMobileView ? 1 : isTabletView ? 2 : 3;

      isInlineDisplayMode =
        (choices.length - 1) * 16 +
          (choices as IMultipleChoiceQuestionView['choiceList']).reduce(
            (acc: number, choice) => {
              let choiceValue: string | number;
              if (useTags) {
                choiceValue = Number(choice);
              } else {
                choiceValue = choice.value;
              }
              const textItem = useTags
                ? tags?.[choiceValue]?.text || choice?.text
                : choice.text;
              acc = acc + (textItem?.length || 0) + 56;
              return acc;
            },
            0
          ) <
        this.state.windowWidth - 140;
    }

    return (
      // hide distinctive questions when isUnique => true
      <QuestionWrapper
        isUniqueQuestion={hiddenForBeingDistinctive}
        disabled={disabled}
      >
        <TitleInput
          fields={question.fields}
          hint={question.hint}
          required={finalRequired}
          title={question.title}
          tags={question.tags}
          allTags={tags}
          indexNumber={question.indexOrder}
          survey={this.props.survey}
          answers={this.props.answers}
          questions={this.props.questions}
        />
        <QuestionBody>
          {hasDepth && choices.length ? (
            <div className="flex flex-wrap gap-3">
              {Array.from(
                { length: (question.goDeepInTags || 0) + 1 },
                (_, ind) => ind
              )
                .filter(
                  (index) => index <= (answer?.selected?.length || -1) + 1
                )
                .map((index) => {
                  const optionList =
                    index === 0
                      ? Object.values(tags || {})
                          .filter((tag: ITagView) =>
                            (choices as number[]).includes(Number(tag.id))
                          )
                          .map((tag: ITagView) => ({
                            label: tag.text,
                            value: tag.id.toString(),
                          }))
                      : Object.values(tags || {})
                          .filter(
                            (tag: ITagView) =>
                              tag.parentId ===
                              Number(answer?.selected[index - 1])
                          )
                          .map((tag: ITagView) => ({
                            label: tag.text,
                            value: tag.id.toString(),
                          }));
                  if (optionList.length === 0) {
                    return null;
                  }
                  return (
                    <Select
                      key={index}
                      label={question.levelTitles?.[index] || ''}
                      multiChoice={false}
                      isRequired={draft ? false : isRequired}
                      onChangeValues={(values) =>
                        this.handleSelectChange(values, index)
                      }
                      options={optionList}
                      values={Object.values(tags || {})
                        .filter(
                          (tag: ITagView) =>
                            tag.id === Number(answer?.selected[index])
                        )
                        .map((tag: ITagView) => ({
                          label: tag.text,
                          value: tag.id.toString(),
                        }))}
                      containerStyle={{ width: '30%' }}
                    />
                  );
                })}
            </div>
          ) : viewMode === 'popup' ? (
            <>
              {answer && answer.error && answer.error.emptyChoices && (
                <div>
                  <span className="red-text">{answer.error.emptyChoices}</span>
                </div>
              )}
              <Select
                isRequired={draft ? false : finalRequired}
                multiChoice={isMultiSelect}
                disabled={disabled}
                onChangeValues={this.handleSelectChange}
                values={
                  answer &&
                  (useTags
                    ? answer.selected &&
                      answer.selected.reduce((tmp: IOption[], tag) => {
                        const tagId = Number(tag);
                        const label = tags?.[tagId]?.text;
                        if (label !== null) {
                          tmp.push({
                            value: tagId,
                            label: label ?? '',
                          });
                        }
                        return tmp;
                      }, [])
                    : answer.value
                      ? [
                          {
                            value: answer.value,
                            label: (
                              (
                                choices as IMultipleChoiceQuestionView['choiceList']
                              ).find((c) => c.value === answer.value) || {
                                text: answer.value,
                              }
                            ).text,
                          } as IOption,
                        ]
                      : answer.values &&
                        answer.values.reduce((tmp: IOption[], answerValue) => {
                          const choice = (
                            choices as IMultipleChoiceQuestionView['choiceList']
                          ).find((c) => c.value === answerValue);
                          if (choice) {
                            tmp.push({
                              value: answerValue,
                              label: choice.text,
                            });
                          }
                          return tmp;
                        }, []))
                }
                options={(
                  choices as IMultipleChoiceQuestionView['choiceList']
                ).reduce((tmp: IOption[], choice) => {
                  if (useTags) {
                    const value = Number(choice);
                    tmp.push({
                      value,
                      label: tags?.[value]?.text ?? '',
                    });
                  } else {
                    tmp.push({
                      value: choice.value,
                      label: choice.text,
                    });
                  }
                  return tmp;
                }, [])}
              />
            </>
          ) : isMultiSelect ? (
            <div className="flex gap-x-5 gap-y-1 items-center flex-wrap py-2 relative">
              <div style={{ opacity: '0', position: 'absolute' }}>
                <Input
                  name={`${question.id}-value-requirement`}
                  id={`${question.id}-hidden-requirement`}
                  type="text"
                  required={
                    draft
                      ? false
                      : finalRequired
                        ? useTags
                          ? !answer?.selected || answer?.selected.length === 0
                          : !answer?.values || answer?.values.length === 0
                        : false
                  }
                />
              </div>
              {(choices as IMultipleChoiceQuestionView['choiceList']).map(
                (choice) => {
                  let choiceValue: string | number;
                  if (useTags) {
                    choiceValue = Number(choice);
                  } else {
                    choiceValue = choice.value;
                  }

                  return (
                    <span key={choiceValue}>
                      <Input
                        label={
                          useTags
                            ? tags?.[choiceValue]?.text || choice?.text
                            : choice.text
                        }
                        disabled={disabled}
                        id={`${question.id}-${choiceValue}`}
                        name={`${question.id}-value`}
                        type="checkbox"
                        value={choiceValue}
                        style={{
                          position: 'initial',
                          width: '1px',
                          height: '1px',
                        }}
                        checked={
                          answer
                            ? useTags
                              ? answer.selected &&
                                (answer.selected.includes(
                                  choiceValue as number
                                ) ||
                                  answer.selected.includes(
                                    choiceValue.toString() as any
                                  ))
                              : answer.value === choiceValue ||
                                (answer.values &&
                                  (answer.values.includes(
                                    choiceValue as string
                                  ) ||
                                    answer.values.includes(
                                      choiceValue.toString()
                                    )))
                            : false
                        }
                        onChange={(e) => {
                          this.handleChange(e.target.value, choiceValue);
                        }}
                      />
                      {/*<label*/}
                      {/*  htmlFor={`${question.id}-${choiceValue}`}*/}
                      {/*  className={styles.checkboxLabel}*/}
                      {/*>*/}
                      {/*  {useTags ? tags![choiceValue].text : choice.text}*/}
                      {/*</label>*/}
                    </span>
                  );
                }
              )}
            </div>
          ) : (
            <QuestionChoiceContainer
              colNumber={colNum}
              isInline={isMobileView ? false : isInlineDisplayMode}
            >
              {(choices as IMultipleChoiceQuestionView['choiceList']).map(
                (choice) => {
                  let choiceValue: string | number;
                  if (useTags) {
                    choiceValue = Number(choice);
                  } else {
                    choiceValue = choice.value;
                  }
                  const isChecked =
                    answer &&
                    (useTags
                      ? answer.selected &&
                        (answer.selected.includes(choiceValue as number) ||
                          answer.selected.includes(
                            choiceValue.toString() as any
                          ))
                      : answer.value
                        ? answer.value === choiceValue
                        : answer.values &&
                          (answer.values.includes(choiceValue as string) ||
                            answer.values.includes(choiceValue.toString())));
                  return (
                    <div
                      key={`${question.id}-${choiceValue}`}
                      className="flex items-center"
                    >
                      <Input
                        label={
                          useTags
                            ? tags?.[choiceValue]?.text || choice?.text
                            : choice.text
                        }
                        disabled={disabled}
                        id={`${question.id}-${choiceValue}`}
                        name={`${question.id}-value`}
                        type="radio"
                        value={choiceValue}
                        checked={!!isChecked}
                        required={draft ? false : finalRequired}
                        onChange={(e) =>
                          this.handleChange(e.target.value, choiceValue)
                        }
                        className="with-gap"
                        style={{
                          position: 'initial',
                          width: '1px',
                          height: '1px',
                        }}
                      />
                      {isChecked && !disabled && (
                        <RemoveSelectedIcon
                          data-choice-value={choiceValue}
                          data-use-tags={useTags}
                          onClick={this.handleRemoveSelectedChoice}
                          type="x"
                        />
                      )}
                    </div>
                  );
                }
              )}
            </QuestionChoiceContainer>
          )}

          {descriptionStatus && (
            <DescriptionInput
              question={question}
              survey={this.props.survey}
              open={!this.state.addDescription}
              handler={this.descriptionHandler}
              id={question.id}
              text={answer && answer.description}
              onChangeInput={
                !disabled
                  ? this.handleChange
                  : () => {
                      /*do nothing*/
                    }
              }
              disabled={disabled}
            />
          )}
        </QuestionBody>
      </QuestionWrapper>
    );
  }
}

export default MultiChoiceQuestion;
